import { Injectable, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
  ApiService,
  DialogService,
  EntityCacheProvider,
  Device,
  TransformationService,
  LmapService,
  TestAssembleService,
  TestServerConfigService
} from '@nrwl/common-lib';
import { BaseSetupService, BaseSetupTab, setupConfig } from '@nrwl/test-setup';
import { Subject } from 'rxjs';
import { Y1731ConfigFieldMapping as ConfigFieldMapping, vlanConfigValue } from './models/models';

@Injectable({
  providedIn: 'root'
})
export class Y1731SetupService extends BaseSetupService implements OnDestroy {
  state: ActivatedRoute;
  entityCache: EntityCacheProvider;
  transformService: TransformationService;
  lmapService: LmapService;
  testAssembleService: TestAssembleService;
  serverConfigService: TestServerConfigService;
  tabDataChangeEvents = new Subject<any>();
  agentChangeEvents = new Subject<any>();
  testTaskName = 'y1731';
  deviceModel = 'vPMA';
  selectedOriginator: Device;
  selectedDestination: Device;
  tenantId = 'pass.local';
  private readonly unsubscribe: Subject<void> = new Subject();
  constructor(
    apiService: ApiService,
    state: ActivatedRoute,
    dialogService: DialogService,
    dialog: MatDialog,
    entityCache: EntityCacheProvider,
    transformService: TransformationService,
    lmapService: LmapService,
    testAssmebleService: TestAssembleService,
    serverConfigService: TestServerConfigService
  ) {
    super(apiService, dialogService, dialog);
    this.state = state;
    this.tabClass = BaseSetupTab;
    this.entityCache = entityCache;
    this.transformService = transformService;
    this.lmapService = lmapService;
    this.testAssembleService = testAssmebleService;
    this.serverConfigService = serverConfigService;
  }

  openTestSetup(
    setupId: string,
    setupConfig: setupConfig,
    defaultParamsById = {},
    onSuccess = () => {},
    onFail = () => {},
    //eslint-disable-next-line
    agentBasedConfig: any
  ): void {
    super.openTestSetup(
      setupId,
      setupConfig,
      defaultParamsById,
      onSuccess,
      onFail,
      agentBasedConfig
    );
  }

  getAllTestParams(): any {
    this.output['soam-pm-config'] = this.getY1731TestConfig();
    return this._getFormatOutput();
  }

  getY1731TestConfig(): any {
    const testconfig = {};
    testconfig['mac-address'] = this.output[ConfigFieldMapping.SRC_MAC_ADDRESS];
    testconfig['mep-id'] = this.output[ConfigFieldMapping.MEP_ID];
    testconfig['flow'] = this.output[ConfigFieldMapping.FLOWS];
    return testconfig;
  }

  createLMAPObject(dataModel: any): any {
    const testPointA = this.output[ConfigFieldMapping.SELECTED_ORIGINATOR]?.deviceData;
    const testPointB = this.output[ConfigFieldMapping.SELECTED_DESTINATION]?.deviceData;
    testPointA['id'] = testPointA.uniqueId;
    if (testPointB) {
      testPointB['id'] = testPointB.uniqueId;
    }
    const serverConfig = this.serverConfigService.getCollectorConfig();
    const collectorURL = this.lmapService.buildCollectorPostUrl(serverConfig, this.deviceModel);
    const testSchedule = this.createTestSchedule(testPointA, collectorURL, dataModel);
    const controllerConfig = this.createControllerConfig(testPointA, testPointB, testSchedule);
    return controllerConfig;
  }

  createTestSchedule(testPointA: any, collectorURL: string, dataModel: any): any {
    const tagOptions: Array<string> = ['monitor-type:on-going'];
    const schedObj = { repeatInterval: this.output[ConfigFieldMapping.PM_REPORT_INTERVAL] };
    // create the test schedule
    const testScheduleName = this.output[ConfigFieldMapping.ALIAS];
    const testActionName = testScheduleName + '-action';
    let testEventName = testScheduleName + '-schedule-event';

    // For start immediate and repeat once test
    if (!schedObj.repeatInterval) {
      testEventName = 'immediate';
    }

    const allTestParams = {};
    allTestParams['soam-pm-viavi:y1731'] = dataModel;

    const actionOption = {
      id: this.testTaskName,
      value: JSON.stringify(allTestParams)
    };

    let optionArray = [];
    optionArray.push(actionOption);

    if (testPointA.phyId !== '') {
      const actionOptionPhy = {
        id: 'phy-id',
        value: testPointA.phyId
      };
      optionArray.push(actionOptionPhy);
    }
    optionArray = [...optionArray, ...this.getOptions()];
    const tags = this.output.scheduler?.repeatInterval ? tagOptions : null;
    const name = this.testTaskName + '-initiator';
    const action = this.lmapService.createAction(testActionName, name, optionArray, null, tags);
    const actionArray = [];
    actionArray.push(action);
    // while report schedule references could be created here they will be added
    //automatically to this schedule via addResultScheduleAndEvent()
    const testSchedule = this.lmapService.createSchedule(
      testScheduleName,
      testEventName,
      actionArray
    );

    // build the LMAP configuration (test schedule + result schedules + all events other than those predefined by MA such as immediate)
    let lmapified = this.lmapService.addSchedule(testSchedule);
    this.lmapService.CONST.resultFormatType = 'v2';
    // final results are mandatory
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'immediate',
      'final',
      collectorURL
    );
    // pm results are technically optional, but mandatory for us
    // set to 1 second interval
    lmapified = this.lmapService.addResultScheduleAndEvent(
      lmapified,
      testScheduleName,
      testEventName,
      'periodic',
      'pm',
      collectorURL,
      schedObj.repeatInterval,
      null,
      null
    );
    let updateEvent = lmapified['ietf-lmap-control:lmap']?.events?.event[1];
    if (updateEvent.periodic.interval) {
      updateEvent.periodic.interval = schedObj.repeatInterval;
    }
    lmapified['ietf-lmap-control:lmap'].events.event = [updateEvent];
    return lmapified;
  }

  createControllerConfig(testPointA, testPointB, lmapified): any {
    const controllerConfig = this.testAssembleService.buildPrimaryTestConfig(
      testPointA,
      this.testTaskName,
      lmapified
    );
    controllerConfig.test['test-list-data'] = {};
    controllerConfig.test['test-list-data']['tpInfo'] = [
      {
        type: 'primary',
        id: testPointA.uniqueId,
        ip: testPointA.mgmtIPaddress
      }
    ];
    controllerConfig.test['extended-config'] = {};
    controllerConfig.test['test-list-data']['interval'] = this.output.scheduler?.repeatInterval;
    //controllerConfig.test['test-list-data']['mode'] = this.output[ConfigFieldMapping.TEST_MODE];
    controllerConfig.test['test-list-data']['customer'] = {
      name: this.output[ConfigFieldMapping.CUSTOMER],
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['extended-config']['customerInfo'] = {
      name: this.output[ConfigFieldMapping.CUSTOMER],
      contact: this.output.contact,
      email: this.output.email,
      phone: this.output.phone
    };
    controllerConfig.test['extended-config']['technicianInfo'] = {
      name: this.output[ConfigFieldMapping.TECHINICIAN],
      id: this.output.technicianId
    };
    controllerConfig.test['extended-config']['testInfo'] = {
      name: this.output[ConfigFieldMapping.ALIAS],
      comments : this.output.comments
    };
    controllerConfig.test['extended-config']['testPoints'] = [
      {
        tag: 'Test Point A',
        selected: true,
        type: 'inventory',
        id: testPointA.id,
        testPortPhyId: testPointA.phyId,
        name: testPointA.nameId,
        vendor: 'viavi',
        model: testPointA.model.model,
        version: testPointA.params.find(param => param.name === 'version').value,
        ip: testPointA.mgmtIPaddress,
        port: '443'
      }
    ];
    return controllerConfig;
  }

  getOptions(): any {
    let vlanConfig = [];
    if (this.output[ConfigFieldMapping.VLAN_CONFIG] === vlanConfigValue.Q_IN_Q) {
      vlanConfig = [
        {
          id: 'vlan-id',
          value: this.output[ConfigFieldMapping.SVLAN_ID].toString()
        },
        {
          id: 'vlan-priority',
          value: this.output[ConfigFieldMapping.SVLAN_PRIORITY].toString()
        },
        {
          id: 'inner-vlan-id',
          value: this.output[ConfigFieldMapping.CVLAN_ID].toString()
        },
        {
          id: 'inner-vlan-priority',
          value: this.output[ConfigFieldMapping.CVLAN_PRIORITY].toString()
        },
        {
          id: 'tpid',
          value: this.output[ConfigFieldMapping.CVLAN_ETHER_TYPE].toString()
        }
      ];
    } else if (this.output[ConfigFieldMapping.VLAN_CONFIG] === vlanConfigValue.SINGLE_VLAN) {
      vlanConfig = [
        {
          id: 'vlan-id',
          value: this.output[ConfigFieldMapping.SVLAN_ID].toString()
        },
        {
          id: 'vlan-priority',
          value: this.output[ConfigFieldMapping.SVLAN_PRIORITY].toString()
        }
      ];
    }
    let options = [];
    if (this.output[ConfigFieldMapping.PERSIST_AFTER_REBOOT]) {
      options.push({
        id: 'persist',
        value: this.output[ConfigFieldMapping.PERSIST_AFTER_REBOOT].toString()
      });
    }
    options = [...options, ...vlanConfig];
    return options;
  }

  getTestOptionsForSaveTemplate(): any {
    const testOptions = {};
    const interval = { interval: this.output[ConfigFieldMapping.PM_REPORT_INTERVAL] };
    const listOfOptions = this.getOptions();
    listOfOptions.forEach(opt => {
      testOptions[opt.id] = opt.value;
    });
    return { ...testOptions, ...interval };
  }

  ngOnDestroy(): void {
    this.unsubscribe.unsubscribe();
  }

  //------------------------------------------------ Wizard control ----------------------------------------------------

  toggleTabChange(index: number): void {
    super.toggleTabChange(index);
    this.tabDataChangeEvents.next(index);
  }

  setOriginatorAgent(device: Device): void {
    this.selectedOriginator = device;
    this.agentChangeEvents.next(device);
  }
  getOriginatorAgent(): any {
    return this.selectedOriginator;
  }
}
